/**
 * Workflow API Service
 */

import { apiRequest } from "../../api/apiClient";
import type {
  Workflow,
  CreateWorkflowDto,
  UpdateWorkflowDto,
  CreateWorkflowStageDto,
  UpdateWorkflowStageDto,
  ApproveWorkflowActionDto,
  QueryWorkflowActionDto,
  RejectWorkflowActionDto,
  WorkflowActionResponse,
  CurrentStageRoleResponse,
  RequeryRemark,
  AppliedWorkflowAction,
} from "../../types/workflow.types";

const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "API request failed");
  }

  // Check if response has content before parsing JSON
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }

  // For 204 No Content or other responses without JSON body
  return null;
};

export const workflowService = {
  // Workflows
  getWorkflows: async (): Promise<Workflow[]> => {
    const response = await apiRequest("/Workflows", {
      method: "GET",
    });
    return handleResponse(response);
  },

  getWorkflowById: async (id: number): Promise<Workflow> => {
    const response = await apiRequest(`/Workflows/${id}`, {
      method: "GET",
    });
    return handleResponse(response);
  },

  getWorkflowsByModule: async (moduleId: number): Promise<Workflow[]> => {
    const response = await apiRequest(`/Workflows/module/${moduleId}`, {
      method: "GET",
    });
    return handleResponse(response);
  },

  createWorkflow: async (data: CreateWorkflowDto): Promise<Workflow> => {
    const response = await apiRequest("/Workflows", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  updateWorkflow: async (
    id: number,
    data: UpdateWorkflowDto,
  ): Promise<Workflow> => {
    const response = await apiRequest(`/Workflows/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  deleteWorkflow: async (id: number): Promise<void> => {
    const response = await apiRequest(`/Workflows/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to delete workflow");
    }
  },

  // Workflow Stages
  createStage: async (
    data: CreateWorkflowStageDto & { workflowId: number },
  ): Promise<any> => {
    console.log("Creating stage with data:", data);
    const payload = {
      id: 0,
      workflowId: data.workflowId,
      roleId: data.roleId,
      name: data.name,
      description: data.description || "",
      stageOrder: data.stageOrder,
      useSignature: data.useSignature,
      required: data.isActive,
      isActive: data.isActive,
      createdAt: new Date().toISOString(),
      createdBy: 0,
      updatedAt: new Date().toISOString(),
      updatedBy: 0,
    };
    const response = await apiRequest(`/Workflows/stages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    console.log("Create stage response:", response.status, response.statusText);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Create stage error:", errorData);
      throw new Error(
        errorData.message ||
          `API request failed: ${response.status} ${response.statusText}`,
      );
    }
    return response.json();
  },

  updateStage: async (
    id: number,
    data: UpdateWorkflowStageDto,
  ): Promise<any> => {
    const payload = {
      id: data.id,
      roleId: data.roleId,
      name: data.name,
      description: data.description || "",
      stageOrder: data.stageOrder,
      useSignature: data.useSignature,
      paymentRequired: data.paymentRequired,
      isActive: data.isActive,
    };
    const response = await apiRequest(`/Workflows/stages/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    return handleResponse(response);
  },

  deleteStage: async (id: number): Promise<void> => {
    const response = await apiRequest(`/Workflows/stages/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to delete stage");
    }
  },

  // Workflow Actions
  approve: async (
    data: ApproveWorkflowActionDto,
  ): Promise<WorkflowActionResponse> => {
    const isCertificateApproval =
      data.entityName?.toLowerCase() === "certificateapplications";

    const response = await apiRequest(
      isCertificateApproval
        ? `/CertificateApplications/${data.recordId}/approve`
        : "/WorkflowActions/approve",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    );
    return handleResponse(response);
  },

  query: async (
    data: QueryWorkflowActionDto,
  ): Promise<WorkflowActionResponse> => {
    const isCertificateApproval =
      data.entityName?.toLowerCase() === "certificateapplications";

    const response = await apiRequest(
      isCertificateApproval
        ? `/CertificateApplications/${data.recordId}/query`
        : "/WorkflowActions/query",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    );
    return handleResponse(response);
  },

  reject: async (
    data: RejectWorkflowActionDto,
  ): Promise<WorkflowActionResponse> => {
    const isCertificateApproval =
      data.entityName?.toLowerCase() === "certificateapplications";

    const response = await apiRequest(
      isCertificateApproval
        ? `/CertificateApplications/${data.recordId}/reject`
        : "/WorkflowActions/reject",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(
          isCertificateApproval
            ? {
                workflowId: data.workflowId,
                entityName: data.entityName,
                recordId: data.recordId,
                reason: data.comments,
              }
            : data,
        ),
      },
    );
    return handleResponse(response);
  },

  getRequeryRemarks: async (
    entityName: string,
    recordId: number,
  ): Promise<RequeryRemark[]> => {
    const response = await apiRequest(
      `/WorkflowActions/requery-remarks?entityName=${encodeURIComponent(entityName)}&recordId=${recordId}`,
      {
        method: "GET",
      },
    );

    const payload = await handleResponse(response);
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray((payload as any)?.data)
        ? (payload as any).data
        : Array.isArray((payload as any)?.items)
          ? (payload as any).items
          : [];

    return items.map((item: any, index: number) => ({
      id: Number(item?.id ?? item?.workflowActionId ?? index + 1),
      remark: String(
        item?.remark ??
          item?.comments ??
          item?.query ??
          item?.requeryRemark ??
          "",
      ),
      createdAt: String(
        item?.actionDate ??
          item?.createdAt ??
          item?.date ??
          item?.updatedAt ??
          "",
      ),
    }));
  },

  getAppliedActions: async (
    entityName: string,
    recordId: number,
  ): Promise<AppliedWorkflowAction[]> => {
    const response = await apiRequest(
      `/WorkflowActions/applied-actions?entityName=${encodeURIComponent(entityName)}&recordId=${recordId}`,
      {
        method: "GET",
      },
    );

    const payload = await handleResponse(response);
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray((payload as any)?.data)
        ? (payload as any).data
        : Array.isArray((payload as any)?.items)
          ? (payload as any).items
          : [];

    return items.map((item: any, index: number) => ({
      id: Number(item?.id ?? item?.workflowActionId ?? index + 1),
      workflowStageId:
        item?.workflowStageId !== undefined
          ? Number(item.workflowStageId)
          : undefined,
      roleName: item?.roleName ? String(item.roleName) : undefined,
      stageName: item?.stageName ? String(item.stageName) : undefined,
      action: item?.action ? String(item.action) : undefined,
      comments: item?.comments ? String(item.comments) : undefined,
      actionByName: item?.actionByName ? String(item.actionByName) : undefined,
      actionDate: item?.actionDate ? String(item.actionDate) : undefined,
    }));
  },

  isAtMyApprovalLevel: async (
    workflowId: number,
    recordId: number,
  ): Promise<boolean> => {
    const response = await apiRequest(
      `/Workflows/at-my-approval-level?workflowId=${workflowId}&recordId=${recordId}`,
      {
        method: "GET",
      },
    );
    return handleResponse(response);
  },

  getCurrentStageRole: async (
    workflowId: number,
    recordId: number,
  ): Promise<CurrentStageRoleResponse> => {
    const response = await apiRequest(
      `/Workflows/current-stage-role?workflowId=${workflowId}&recordId=${recordId}`,
      {
        method: "GET",
      },
    );
    return handleResponse(response);
  },
};
